<template>
    <tailwind-layout title="Maintenance: Edit Property">
        <template #header>
            <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Maintenance: Edit Property</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" :href="route('maintenance.index')">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>
        </template>
        <div class="overflow-hidden shadow-xl rounded-lg p-6 bg-white">
            <form @submit.prevent="update" class="space-y-8 divide-y divide-gray-200">
                <div class="space-y-8 divide-y divide-gray-200">
                    <div class="space-y-8 divide-y divide-gray-200">
                    <div>
                        <div>
                            <h3 class="text-lg leading-6 font-medium text-gray-900">
                                Maintenance Property Details
                            </h3>
                        </div>
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div class="sm:col-span-2">
                                <label for="existing_property" class="block text-sm font-medium text-gray-700">
                                    Use Existing Properties
                                </label>
                                <div class="mt-1">
                                    <select v-model="form.existing_property" @change="useExistingProperty($event)" id="existing_property" data-demo-target="use-existing-address" class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                        <option value="">Select Any Property</option>
                                        <option v-for="(property, index) in existing_properties" :value="property.id">{{ property.full_address }}</option>
                                    </select>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="owner_name" v-bind:class="(form.errors.owner_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Owner/Landlord Name
                                </label>
                                <div class="mt-1">
                                    <input id="owner_name" v-model="form.owner_name" type="text" data-demo-target="owner_name" v-bind:class="(form.errors.owner_name)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p v-text="form.errors.owner_name" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="address-lookup" class="block text-sm font-medium text-gray-700">
                                    Address Lookup
                                </label>
                                <div class="mt-1">
                                    <div class="mt-1 relative rounded-md">
                                        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <font-awesome-icon :icon="faMapMarkerAlt" class="text-gray-400" />
                                        </div>
                                        <GMapAutocomplete
                                            id="address_lookup"
                                            ref="autocompleteRef"
                                            placeholder="Start typing address..."
                                            @place_changed="initAutocomplete"
                                            :options="{
                                                componentRestrictions: { country: ['US', 'VI', 'AS', 'GU', 'PR', 'MP'] }
                                            }"
                                            v-bind:class="'py-0.5 h-10 px-1 mt-1 shadow-sm h-8 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md pl-10'"
                                            data-demo-target="address_lookup"
                                        >
                                        </GMapAutocomplete>
                                    </div>
                                    <p class="text-xs text-gray-500">Please include postal code for accuracy.</p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="address-1" v-bind:class="(form.errors.address_1)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 1 <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="address_1" v-model="form.address_1" readonly :error="form.errors.address_1" type="text" v-bind:class="(form.errors.address_1)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md bg-gray-100':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-100  bg-gray-100'">
                                    <p v-text="form.errors.address_1" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="address-2" v-bind:class="(form.errors.address_2)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 2
                                </label>
                                <div class="mt-1">
                                    <input id="address-2" v-model="form.address_2" type="text" v-bind:class="(form.errors.address_2)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'">
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="city" v-bind:class="(form.errors.city)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    City <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="city" v-model="form.city" readonly type="text" v-bind:class="(form.errors.city)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md  bg-gray-100'">
                                    <p v-text="form.errors.city" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="state" v-bind:class="(form.errors.state)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    State <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input v-model="form.state" type="hidden">
                                    <select id="state" disabled v-bind:value="form.state" v-model="form.state" :error="form.errors.state" v-bind:class="(form.errors.state)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-gray-100'">
                                        <option v-for="(state, index) in states" :value="index">{{ state }}</option>
                                    </select>
                                    <p v-text="form.errors.state" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="zip_code" v-bind:class="(form.errors.zip_code)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Zip Code <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="zip_code" v-model="form.zip_code" readonly type="text" v-bind:class="(form.errors.zip_code)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-100'">
                                    <p v-text="form.errors.zip_code" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                        </div>
                    </div>
            </div>
                </div>

                <div>
                    <div class="sm:col-span-6">
                        <h3 class="text-lg leading-6 font-medium text-gray-900 mt-2">
                            Tenant Maintenance Instructions
                        </h3>
                        <p class="text-xs leading-6 font-medium text-gray-500">These are the instructions your tenant will see on their dashboard once you invite them to the portal.</p>
                    </div>
                    <div class="mt-7 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div class="sm:col-span-2">
                            <label for="tenant_email"
                                v-bind:class="(form.errors.tenant_email)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                Tenant Email
                            </label>
                            <div class="mt-1 relative rounded-md">
                                <div
                                    class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                    v-bind:class="(form.errors.tenant_email)?'top-3':'inset-y-0'">
                                    <font-awesome-icon :icon="faEnvelope" class="text-gray-400" />
                                </div>
                                <input id="tenant_email" v-model="form.tenant_email" type="email" data-demo-target="invite-tenant"
                                    v-bind:class="(form.errors.tenant_email)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'"
                                >
                                <p v-text="form.errors.tenant_email" class="text-red-500 text-sm"></p>
                            </div>
                        </div>
                        <div class="sm:col-span-2">
                            <label for="tenant_message"
                                v-bind:class="(form.errors.tenant_message)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                                Email sent to Tenant
                                <tool-tip
                                    :title="'Email sent to Tenant'"
                                    :description="'This is the message you want your tenant to receive once they put in a request. The below message is the default message.'"
                                ></tool-tip>
                            </label>
                            <div class="mt-1 relative rounded-md">
                                <textarea id="tenant_message" rows="3" v-model="form.tenant_message"
                                    v-bind:class="(form.errors.tenant_message)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"
                                ></textarea>
                                <p v-text="form.errors.tenant_message" class="text-red-500 text-sm"></p>
                            </div>
                        </div>

                        <!-- <div class="sm:col-span-2">
                            <label class="text-sm font-medium text-gray-700">Request Types</label>
                            <div class="relative mt-0">
                                <button type="button" @click="toggleEmergencyDropdown" ref="emergencyDropdownButton"
                                    class="w-full flex justify-between items-center border rounded-md bg-white shadow-sm px-3 py-2 text-sm text-gray-700 focus:ring-indigo-500 focus:border-indigo-500">
                                    <span v-if="form.selectedEmergencies.length && form.selectedEmergencies.some(id => getEmergencyName(id) !== 'Unknown')">
                                        {{ form.selectedEmergencies.map(id => getEmergencyName(id)).filter(name => name !== 'Unknown').join(', ') }}
                                    </span>
                                    <span v-else class="text-gray-400">No emergency types </span>
                                    <svg class="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </button>

                                <div v-if="dropdownOpenEmergencies && maintenanceEmergencies.length" ref="emergencyDropdownMenu"
                                    class="absolute left-0 mt-2 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden z-50"
                                    style="max-height: 200px; overflow-y: auto;">

                                    <div v-for="emergency in maintenanceEmergencies" :key="emergency.id"
                                        class="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                                        <input type="checkbox" v-model="form.selectedEmergencies" :value="emergency.id"
                                            class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                        <label class="ml-2 text-sm text-gray-900">{{ emergency.name }}</label>
                                        <span class="ml-2 text-sm text-gray-500">{{ emergency.description || '' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div> -->


                        <div class="sm:col-span-2">
                            <label for="repair_amount"
                                v-bind:class="(form.errors.repair_amount)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                Tenant is responsible for any repairs up to ($)
                            </label>
                            <div class="mt-1 relative rounded-md">
                                <input id="repair_amount" v-model="form.repair_amount" type="number" step="0.01"
                                    v-bind:class="(form.errors.repair_amount)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:ring-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'">
                                <p v-text="form.errors.repair_amount" class="text-red-500 text-sm"></p>
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="specific_instructions"
                                v-bind:class="(form.errors.specific_instructions)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                                Specific Instructions
                                <tool-tip
                                    :title="'Specific Instructions'"
                                    :description="'This is where you tell your tenant how you want them to handle the maintenance request. For example, Call Home Warranty Company for plumbing or electrical issues.'"
                                ></tool-tip>
                            </label>
                            <div class="mt-1 relative rounded-md">
                                <textarea
                                        id="specific_instructions"
                                        rows="2" v-model="form.specific_instructions"
                                        :error="form.errors.specific_instructions"
                                        placeholder="Any other specific instructions for the tenant"
                                        v-bind:class="(form.errors.specific_instructions)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:ring-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"
                                    >
                                    </textarea>
                                <p v-text="form.errors.specific_instructions" class="text-red-500 text-sm"></p>
                            </div>
                        </div>

                        <!-- <div v-if="!maintenanceEmergencies || maintenanceEmergencies.length === 0" class="rounded-md bg-yellow-50 p-4 mt-2">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <font-awesome-icon icon="fa-info-circle" class="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                </div>
                                <div class="ml-3 text-sm text-yellow-700">
                                    <p>
                                        If you've not set up Emergency Options, please visit
                                        <span class="font-bold">Maintenance > Emergency Options</span> Menu or
                                        <inertia-link :href="route('maintenance.emergency-options.index')" class="text-blue-600">
                                            Click here.
                                        </inertia-link>
                                    </p>
                                </div>
                            </div>
                        </div> -->

                        <div class="sm:col-span-2">
                            <label for="guidelines" v-bind:class="(form.errors.guidelines)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                                Emergency Guidlines
                                <tool-tip
                                        :title="'Emergency Guidlines'"
                                        :description="'Please call landlord directly if there is any water emergency such as a leaking roof, flooding basement, etc.'"
                                    ></tool-tip>
                            </label>
                            <div class="mt-1">
                                <textarea
                                    id="guidelines"
                                    rows="2" v-model="form.guidelines"
                                    :error="form.errors.guidelines"
                                    placeholder="Any guidelines for the tenant"
                                    v-bind:class="(form.errors.guidelines)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'"
                                    data-demo-target="emergency-contact"
                                >
                                </textarea>
                            </div>
                        </div>


                        <div class="sm:col-span-6 border-b pb-0 mb-0">
                                <h3 class="text-lg font-medium text-gray-900">Home Warranty Company</h3>
                            </div>
                            <div class="sm:col-span-6 mt-1">
                                <label class="text-sm font-medium text-gray-700">Tenant may contact directly?</label>
                                <div class="mt-1 flex space-x-4">
                                    <label class="flex items-center space-x-2 cursor-pointer">
                                        <input type="radio" v-model="form.tenant_can_contact_directly" :value="true"
                                            class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                        <span class="text-sm text-gray-700">Yes</span>
                                    </label>
                                    <label class="flex items-center space-x-2 cursor-pointer">
                                        <input type="radio" v-model="form.tenant_can_contact_directly" :value="false"
                                            class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                        <span class="text-sm text-gray-700">No</span>
                                    </label>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label v-bind:class="(form.errors.warranty_company_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Company Name
                                </label>
                                <input type="text" v-model="form.warranty_company_name"
                                    v-bind:class="(form.errors.warranty_company_name)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.warranty_company_name" class="text-red-500 text-sm"></p>
                            </div>
                            <div class="sm:col-span-2">
                                <div>
                                    <label v-bind:class="(form.errors.company_phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">Company Phone Number</label>
                                    <input type="text" v-model="form.company_phone"
                                    v-bind:class="(form.errors.company_phone)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                        <p v-text="form.errors.company_phone" class="text-red-500 text-sm"></p>
                                        <p class="text-gray-500 text-xs">The phone number must be 10 digits. Please remove any dashes or symbols.</p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label v-bind:class="(form.errors.company_website)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Website
                                </label>
                                <input type="text" v-model="form.company_website"
                                    placeholder="https://www.example.com"
                                    v-bind:class="(form.errors.company_website)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.company_website" class="text-red-500 text-sm"></p>
                                <p class="text-gray-500 text-xs">
                                    Please include the https:// before the URL.
                                </p>
                            </div>
                            <div class="sm:col-span-2">
                                <label v-bind:class="(form.errors.contract_number)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Contract Number
                                </label>
                                <input type="text" v-model="form.contract_number"
                                    v-bind:class="(form.errors.contract_number)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.contract_number" class="text-red-500 text-sm"></p>
                            </div>
                            <div class="sm:col-span-2">
                                <label v-bind:class="(form.errors.warranty_additional_instructions)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Additional Instructions
                                </label>
                                <textarea v-model="form.warranty_additional_instructions" rows="2"
                                    v-bind:class="(form.errors.warranty_additional_instructions)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'"
                                    placeholder="Any specific instructions for the tenant regarding the warranty company"></textarea>
                                <p v-text="form.errors.warranty_additional_instructions" class="text-red-500 text-sm"></p>
                            </div>

                    </div>
                </div>

                <div class="pt-5">
                    <div class="flex justify-end">
                        <inertia-link :href="route('maintenance.index')" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Cancel</inertia-link>
                        <form-button :form="form" type="submit" data-demo-target="save-button" class="ml-3">
                            Update
                        </form-button>
                    </div>
                </div>
            </form>
        </div>
    </tailwind-layout>

    <DemoWidget
        :page-identifier="'maintenance.edit'"
        :steps="demoSteps"
        :auto-start="true"
        :show-help-icon="true"
    />
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import RadioGroup from "@/Shared/RadioGroup.vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronLeft, faEnvelope, faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons'
import ToolTip from "@/Shared/ToolTip.vue";
import { getDemoSteps } from '@/Config/demoSteps.js'
import DemoWidget from '@/Components/DemoWidget.vue';
export default {
    props: {
        states: Object,
        property: Object,
        existing_properties: Object,
        maintenanceEmergencies: {
            type: Array,
            default: () => [],
        },
        vendors: {
            type: Array,
            default: () => [],
        },
    },
    components: {
        TailwindLayout,
        FormButton,
        SelectInput,
        ToolTip,
        RadioGroup,
        FontAwesomeIcon,
        DemoWidget
    },
    remember: 'form',
    data() {
        return {
            form: this.$inertia.form({
                existing_property: '',
                owner_name: this.property.owner_name,
                address_1: this.property.address_1,
                address_2: this.property.address_2,
                city: this.property.city,
                state: this.property.state,
                county: this.property.county,
                zip_code: this.property.zip_code,
                tenant_email: this.property.tenant_instructions?.tenant_email ?? '',
                repair_amount: this.property.tenant_instructions?.repair_amount ?? '',
                tenant_message: this.property.tenant_instructions?.tenant_message ?? '',
                specific_instructions: this.property.tenant_instructions?.specific_instructions ?? '',
                guidelines: this.property.tenant_instructions?.guidelines ?? '',
                tenant_can_contact_directly: this.property.tenant_instructions?.tenant_can_contact_directly ?? false,
                warranty_company_name: this.property.tenant_instructions?.warranty_company_name ?? '',
                company_phone: this.property.tenant_instructions?.company_phone ?? '',
                company_website: this.property.tenant_instructions?.company_website ?? '',
                contract_number: this.property.tenant_instructions?.contract_number ?? '',
                warranty_additional_instructions: this.property.tenant_instructions?.warranty_additional_instructions ?? '',
            }),
            dropdownOpenEmergencies: false,
            dropdownOpenVendors: false,
            faChevronLeft,
            faEnvelope,
            faMapMarkerAlt,
            demoSteps: getDemoSteps('maintenance.edit'),
        }
    },

    methods: {
        useExistingProperty(event) {
            const property_id = Number(event.target.value); // Convert to correct type
            if (!property_id) {
                this.form.reset();
            } else {
                const property = this.existing_properties.find(
                    property => property.id === property_id
                );

                if (property) {
                    this.form.owner_name = property.owner_name;
                    this.form.address_1 = property.address_1;
                    this.form.address_2 = property.address_2;
                    this.form.city = property.city;
                    this.form.state = property.state;
                    this.form.county = property.county;
                    this.form.zip_code = property.zip_code;
                    this.form.rent = property.rent;
                }
            }
        },
        update() {
            this.form.put(this.route('maintenance.update', this.property.id)),{
                selectedVendors: this.form.selectedVendors
            }
        },
        initAutocomplete(place){
            // RAC-9990 — see Pages/V2/Properties/AddProperty.vue for the full story.
            // Surface inline error via form.errors.address_1 (existing template binding).
            if (!place?.address_components) {
                this.form.errors.address_1 = "We couldn't read a full address from that result. Please pick a more specific result from the dropdown — make sure it's a street address, not a business or area.";
                return;
            }
            this.form.errors.address_1 = null;

            let street = null;
            let route = null;

            place.address_components.forEach(component => {
                const componentType = component.types[0];
                switch (componentType) {
                    case "street_number": {
                        street = `${component.long_name}`;
                        break;
                    }
                    case "route": {
                        route = ' '+component.short_name;
                        break;
                    }
                    case "postal_code": {
                        this.form.zip_code = `${component.long_name}`;
                        break;
                    }
                    case "locality":
                        this.form.city = component.long_name;
                        break;
                    case "administrative_area_level_2":
                        this.form.county = component.long_name;
                        break;
                    case "administrative_area_level_1": {
                        this.form.state = `${component.short_name}`;
                        break;
                    }
                    case "country": {
                        if(['VI', 'AS', 'GU', 'PR', 'MP'].includes(component.short_name)) {
                            this.form.state = `${component.short_name}`;
                        }
                        break;
                    }
                }
            });
            if(street === null || route === null) {
                this.form.address_1 = null;
                this.form.errors.address_1 = 'Address 1 not found. Enter the full address in Google search.';
            } else {
                this.form.address_1 = `${street} ${route}`
                this.form.errors.address_1 = null;
            }
            this.$refs.autocompleteRef.$refs.input.value = null;
        },
    }
}
</script>
